import { computed, onScopeDispose, ref } from "vue";

import { useNotify } from "@/composition/notification";
import { emitter } from "@/composition/useEmitter";
import { i18n } from "@/plugin/vue-i18n";
import { WorkflowServices } from "@/service/WorkflowServices";
import { useAppStore } from "@/store/app";
import type {
  LinkedWorkflow,
  LinkedWorkflowsPage,
  WorkflowsChangedPayload,
} from "@/types/linkedWorkflows";
import {
  buildLinkedWorkflowsSearchBody,
  parseLinkedWorkflowsResponse,
} from "@/util/linkedWorkflows";

export type LinkedWorkflowsStatus = "idle" | "loading" | "ready" | "error";

/** ES is eventually consistent: optimistic writes win over search results for this long */
export const OPTIMISTIC_OVERRIDE_TTL_MS = 30_000;
/** Waits before each refetch after the host signals a change (covers ES indexing lag) */
export const REFRESH_BACKOFF_MS = [0, 1000, 2000, 4000];
/**
 * Closing hotlinks without a workflow id is usually a no-op (nothing was created), so it
 * gets a short schedule instead of the full backoff
 */
export const HOTLINKS_CLOSED_BACKOFF_MS = [0, 1500];
export const FOCUS_REFRESH_THROTTLE_MS = 10_000;

const LOG_PREFIX = "[connections-workflows]";

/** HTTP status from an axios-style error, if any -- the only error detail we log */
function errorStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const { response } = error as { response?: { status?: unknown } };
  return typeof response?.status === "number" ? response.status : undefined;
}

type Override ={ kind: "rename"; name: string; expiresAt: number } | { kind: "delete"; expiresAt: number };

/** Applies pending optimistic writes over a freshly fetched page and prunes settled ones */
function applyOverrides(page: LinkedWorkflowsPage, overrides: Map<string, Override>) {
  const now = Date.now();
  const workflows: LinkedWorkflow[] = [];
  for (const [id, override] of overrides) {
    if (override.expiresAt <= now) overrides.delete(id);
  }
  for (const workflow of page.workflows) {
    const override = overrides.get(workflow.id);
    if (override?.kind === "delete") continue;
    if (override?.kind === "rename" && override.name !== workflow.name) {
      workflows.push({ ...workflow, name: override.name });
      continue;
    }
    if (override) overrides.delete(workflow.id); // server caught up
    workflows.push(workflow);
  }
  return { ...page, workflows };
}

/** Everything a row renders -- equal fingerprints mean a refresh would change nothing */
const listFingerprint = (rows: LinkedWorkflow[]) =>
  rows
    .map((row) =>
      [row.id, row.name, row.status, row.updatedAt, row.canWrite, row.effectiveBuilder].join("\u0000")
    )
    .join("\u0001");

/**
 * Owns the Connections › Workflow list state. Location and asset scope are read from the
 * handshake-backed app store only -- never from props, the route or user input.
 */
export function useLinkedWorkflows() {
  const store = useAppStore();
  const { sendNotification } = useNotify();
  const { t } = i18n.global;
  const overrides = new Map<string, Override>();

  const workflows = ref<LinkedWorkflow[]>([]);
  /** Rows shown -- drives the Workflow tab badge */
  const count = computed(() => workflows.value.length);
  /** The server has more linked workflows than the single capped search returned */
  const isTruncated = ref(false);
  const status = ref<LinkedWorkflowsStatus>("idle");

  let controller: AbortController | null = null;
  let requestSeq = 0;
  let lastFetchAt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  /** load() calls that have not settled yet */
  let loadsInFlight = 0;
  /** False while a KeepAlive parent has the pane deactivated */
  let active = true;
  /** The host change the running backoff is waiting on */
  let backoffChange: WorkflowsChangedPayload | null = null;
  /** A host change that arrived (or was cut short) while the pane was hidden */
  let pendingChange: WorkflowsChangedPayload | null = null;

  const scope = () => {
    if (disposed) return null;
    const locationId = store.locationId;
    const asset = store.linkedWorkflowsScope;
    if (!locationId || !asset) return null;
    return { locationId, ...asset };
  };

  async function fetchList() {
    const ids = scope();
    if (!ids) return null;
    controller?.abort();
    controller = new AbortController();
    const seq = ++requestSeq;
    const data = await WorkflowServices.searchLinked(
      buildLinkedWorkflowsSearchBody({
        locationId: ids.locationId,
        productType: ids.productType,
        assetId: ids.assetId,
      }),
      controller.signal
    );
    if (seq !== requestSeq) return null;
    return applyOverrides(parseLinkedWorkflowsResponse(data, { locationId: ids.locationId }), overrides);
  }

  /** `quiet` keeps rows and status visible while refetching in the background */
  async function load({ quiet = false }: { quiet?: boolean } = {}) {
    if (!scope()) return;
    const seq = requestSeq + 1;
    if (!quiet || status.value !== "ready") status.value = "loading";
    loadsInFlight++;
    try {
      const result = await fetchList();
      if (!result) return;
      // Identical data keeps the same array, so the table does not rebuild every row
      if (listFingerprint(result.workflows) !== listFingerprint(workflows.value)) {
        workflows.value = result.workflows;
      }
      isTruncated.value = result.truncated;
      status.value = "ready";
      lastFetchAt = Date.now();
    } catch (error: unknown) {
      if (seq !== requestSeq) return; // superseded or aborted
      console.error(`${LOG_PREFIX} load failed`, { status: errorStatus(error) });
      if (!quiet || status.value !== "ready") status.value = "error";
    } finally {
      loadsInFlight--;
    }
  }

  /** Tenant binding: only rows fetched for the handshake location may be renamed or deleted */
  function findOwned(workflowId: string) {
    const ids = scope();
    if (!ids) return null;
    const index = workflows.value.findIndex((workflow) => workflow.id === workflowId);
    const workflow = workflows.value[index];
    if (!workflow || workflow.locationId !== ids.locationId) return null;
    return { workflow, index, locationId: ids.locationId };
  }

  const isOwned = (workflowId: string) => findOwned(workflowId) !== null;

  /** Rename and delete also need write permission -- the menu hiding is not the only guard */
  function findWritable(workflowId: string) {
    const owned = findOwned(workflowId);
    return owned?.workflow.canWrite ? owned : null;
  }

  const expiry = () => Date.now() + OPTIMISTIC_OVERRIDE_TTL_MS;

  async function rename(workflowId: string, nextName: string): Promise<boolean> {
    const owned = findWritable(workflowId);
    const name = nextName.trim();
    if (!owned || !name || name === owned.workflow.name) return false;
    const previous = owned.workflow;
    const patch = (value: LinkedWorkflow) =>
      (workflows.value = workflows.value.map((item) => (item.id === workflowId ? value : item)));
    patch({ ...previous, name });
    overrides.set(workflowId, { kind: "rename", name, expiresAt: expiry() });
    try {
      await WorkflowServices.rename(owned.locationId, workflowId, name);
      sendNotification({
        type: "success",
        title: t("common.workflowRenamed.title"),
        description: t("common.workflowRenamed.description", { name }),
      });
      return true;
    } catch (error: unknown) {
      // Workflow names are user data -- log only the id and status
      console.error(`${LOG_PREFIX} rename failed`, { workflowId, status: errorStatus(error) });
      // Roll back only if no newer rename of this row has taken over since this call
      const override = overrides.get(workflowId);
      if (override?.kind === "rename" && override.name === name) {
        overrides.delete(workflowId);
        const current = workflows.value.find((item) => item.id === workflowId);
        if (current?.name === name) patch({ ...current, name: previous.name });
      }
      sendNotification({
        type: "error",
        title: t("common.workflowRenameFailed.title"),
        description: t("common.workflowRenameFailed.description"),
      });
      return false;
    }
  }

  async function remove(workflowId: string): Promise<boolean> {
    const owned = findWritable(workflowId);
    if (!owned) return false;
    workflows.value = workflows.value.filter((item) => item.id !== workflowId);
    overrides.set(workflowId, { kind: "delete", expiresAt: expiry() });
    try {
      await WorkflowServices.remove(owned.locationId, workflowId);
      sendNotification({
        type: "success",
        title: t("common.workflowDeleted.title"),
        description: t("common.workflowDeleted.description", { name: owned.workflow.name }),
      });
      return true;
    } catch (error: unknown) {
      console.error(`${LOG_PREFIX} delete failed`, { workflowId, status: errorStatus(error) });
      overrides.delete(workflowId);
      const restored = [...workflows.value];
      restored.splice(Math.min(owned.index, restored.length), 0, owned.workflow);
      workflows.value = restored;
      sendNotification({
        type: "error",
        title: t("common.workflowDeleteFailed.title"),
        description: t("common.workflowDeleteFailed.description"),
      });
      return false;
    }
  }

  const cancelRetry = () => {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  };

  /** Quiet refetches on `schedule` until `isDone` passes or the steps run out */
  function refreshUntil(isDone: () => boolean, schedule: number[], step = 0) {
    cancelRetry();
    if (step >= schedule.length) {
      backoffChange = null;
      return;
    }
    retryTimer = setTimeout(async () => {
      retryTimer = null;
      await load({ quiet: true });
      if (disposed || !active) return; // deactivation keeps backoffChange for the catch-up
      if (isDone()) backoffChange = null;
      else refreshUntil(isDone, schedule, step + 1);
    }, schedule[step]);
  }

  const fingerprint = (workflow: LinkedWorkflow | undefined) =>
    workflow ? `${workflow.name}\u0000${workflow.status}\u0000${workflow.updatedAt}` : null;

  function onWorkflowsChanged(change: WorkflowsChangedPayload) {
    if (!active) {
      pendingChange = change; // caught up in setActive(true)
      return;
    }
    backoffChange = change;
    const { workflowId } = change;
    if (!workflowId) {
      // Done only when a workflow that was not listed before shows up. An id-set check, so
      // an optimistic delete or a capped list that never changes size cannot end it early.
      const before = new Set(workflows.value.map((workflow) => workflow.id));
      refreshUntil(
        () => workflows.value.some((workflow) => !before.has(workflow.id)),
        HOTLINKS_CLOSED_BACKOFF_MS
      );
      return;
    }
    const find = () => workflows.value.find((workflow) => workflow.id === workflowId);
    // A workflow edited in the builder is already listed: wait until search re-indexes it
    const before = fingerprint(find());
    refreshUntil(() => {
      const current = fingerprint(find());
      return current !== null && current !== before;
    }, REFRESH_BACKOFF_MS);
  }

  /** Throttled quiet refetch for focus, tab visibility and KeepAlive reactivation */
  function refreshIfStale() {
    if (!active || status.value !== "ready") return;
    // Never cut into a scheduled backoff retry or abort a request that is still running
    if (retryTimer || loadsInFlight > 0) return;
    if (Date.now() - lastFetchAt < FOCUS_REFRESH_THROTTLE_MS) return;
    // visibilitychange and focus usually fire together -- count the attempt, not the response
    lastFetchAt = Date.now();
    void load({ quiet: true });
  }

  function onReturnToTab() {
    if (document.visibilityState === "visible") refreshIfStale();
  }

  /** Called from the pane's onActivated/onDeactivated */
  function setActive(next: boolean) {
    active = next;
    if (!next) {
      // A backoff cut short by hiding the pane resumes on the next activation
      if (backoffChange) pendingChange = pendingChange ?? backoffChange;
      backoffChange = null;
      cancelRetry();
      return;
    }
    const change = pendingChange;
    pendingChange = null;
    // A change missed while hidden bypasses the focus throttle
    if (change) onWorkflowsChanged(change);
    else refreshIfStale();
  }

  emitter.on("workflows:changed", onWorkflowsChanged);
  document.addEventListener("visibilitychange", onReturnToTab);
  window.addEventListener("focus", onReturnToTab);

  onScopeDispose(() => {
    disposed = true;
    requestSeq++;
    controller?.abort();
    cancelRetry();
    emitter.off("workflows:changed", onWorkflowsChanged);
    document.removeEventListener("visibilitychange", onReturnToTab);
    window.removeEventListener("focus", onReturnToTab);
  });

  return { workflows, count, isTruncated, status, load, setActive, isOwned, rename, remove };
}
